/**
=========================================================
* Annotation Portal React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState, useRef, useCallback } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Annotation Portal React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Annotation Portal React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { useTranslation } from "hooks/useTranslation";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  totalRecords,
  onPaginationChange,
}) {
  const { t } = useTranslation();
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      autoResetPage: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageSize(defaultValue || 10), []);

  // Calculate total pages for server-side pagination (used for clamp + UI)
  const isServerSidePagination = totalRecords !== undefined && pagination?.onPageChange;

  // Clamp page index when filtered data shrinks (autoResetPage is disabled)
  useEffect(() => {
    if (isServerSidePagination) return;
    const maxPageIndex = Math.max(0, pageOptions.length - 1);
    if (pageIndex > maxPageIndex) {
      gotoPage(maxPageIndex);
    }
  }, [data.length, pageSize, pageIndex, pageOptions.length, gotoPage, isServerSidePagination]);

  const skipFirstPaginationNotify = useRef(true);
  const paginationRef = useRef(null);

  useEffect(() => {
    if (skipFirstPaginationNotify.current) {
      skipFirstPaginationNotify.current = false;
      return;
    }
    if (typeof onPaginationChange === "function") {
      onPaginationChange({ pageIndex, pageSize });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fire on pagination state (not first paint)
  }, [pageIndex, pageSize, onPaginationChange]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => {
    setPageSize(value);
    // Call custom onChange handler if provided (for server-side pagination)
    if (entriesPerPage && entriesPerPage.onChange) {
      entriesPerPage.onChange(value);
    }
  };

  const totalPages = isServerSidePagination
    ? Math.ceil(totalRecords / pageSize)
    : pageOptions.length;
  const currentPage = isServerSidePagination
    ? pagination?.page !== undefined
      ? pagination.page
      : pageIndex
    : pageIndex;

  const scrollPaginationIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      paginationRef.current?.scrollIntoView({ block: "nearest", behavior: "auto" });
    });
  }, []);

  const goToPageSafe = useCallback(
    (nextIndex) => {
      const max = Math.max(0, totalPages - 1);
      const clamped = Math.min(Math.max(0, nextIndex), max);
      if (isServerSidePagination && pagination?.onPageChange) {
        pagination.onPageChange(null, clamped);
      } else {
        gotoPage(clamped);
      }
      scrollPaginationIntoView();
    },
    [totalPages, isServerSidePagination, pagination, gotoPage, scrollPaginationIntoView]
  );

  // Generate page numbers for server-side or client-side pagination
  const serverPageOptions = isServerSidePagination
    ? Array.from({ length: totalPages }, (_, i) => i)
    : pageOptions;

  // Render the paginations
  const renderPagination = serverPageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => goToPageSafe(Number(option))}
      active={currentPage === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Customized page options starting from 1
  const customizedPageOptions = serverPageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: input }) => {
    const raw = input.value;
    if (raw === "" || raw === undefined) return;
    const pageOneBased = Number(raw);
    if (Number.isNaN(pageOneBased)) return;
    const clampedOneBased = Math.min(Math.max(1, pageOneBased), totalPages);
    goToPageSafe(clampedOneBased - 1);
  };

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Total rows after filtering
  // Use totalRecords prop for server-side pagination, otherwise use rows.length for client-side
  const totalEntries = totalRecords !== undefined ? totalRecords : rows.length;

  // Start index (1-based); when no rows, force 0–0 (never -1)
  const entriesStart = totalEntries === 0 ? 0 : pageIndex * pageSize + 1;

  const entriesEnd =
    totalEntries === 0 ? 0 : Math.min(entriesStart + page.length - 1, totalEntries);

  const paginationBarSx = {
    position: "relative",
    zIndex: 2,
    backgroundColor: (theme) =>
      theme.palette.mode === "dark" ? theme.palette.background.default : "#fff",
    flexShrink: 0,
    pointerEvents: "auto",
    scrollMarginBottom: "5rem",
    pb: 2,
  };

  return (
    <>
      <TableContainer sx={{ boxShadow: "none" }}>
        {entriesPerPage || canSearch ? (
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            {entriesPerPage && (
              <MDBox display="flex" alignItems="center">
                <Autocomplete
                  disableClearable
                  value={pageSize.toString()}
                  options={entries}
                  onChange={(event, newValue) => {
                    setEntriesPerPage(parseInt(newValue, 10));
                  }}
                  size="small"
                  sx={{ width: "5rem" }}
                  renderInput={(params) => <MDInput {...params} />}
                />
                <MDTypography variant="caption" color="secondary">
                  &nbsp;&nbsp;{t("dataTable.entriesPerPage", "entries per page")}
                </MDTypography>
              </MDBox>
            )}
            {canSearch && (
              <MDBox width="12rem" ml="auto">
                <MDInput
                  placeholder={t("dataTable.searchPlaceholder", "Search...")}
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        ) : null}
        <Table {...getTableProps()}>
          <MDBox component="thead">
            {headerGroups.map((headerGroup, key) => (
              <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, idx) => (
                  <DataTableHeadCell
                    key={idx}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    sorted={setSortedValue(column)}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                ))}
              </TableRow>
            ))}
          </MDBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const rowKey = row.original?.id ?? row.id ?? rowIndex;
              return (
                <TableRow
                  key={rowKey}
                  {...row.getRowProps()}
                  sx={{
                    // backgroundColor: key % 2 === 0 ? "#ffffff" : "#f7f9fc",
                    "&:hover": {
                      backgroundColor: "#e4ecffff", // light blue on hover
                      // cursor: "pointer",
                    },
                  }}
                >
                  {row.cells.map((cell, idx) => (
                    <DataTableBodyCell
                      key={idx}
                      noBorder={noEndBorder && rows.length - 1 === rowIndex}
                      align={cell.column.align ? cell.column.align : "left"}
                      {...cell.getCellProps()}
                      sx={{
                        "&:hover": {
                          fontWeight: "bold",
                          color: "black", // light blue on hover
                        },
                      }}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <MDBox
        ref={paginationRef}
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && totalPages === 1 ? 0 : 3}
        sx={paginationBarSx}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              {t("dataTable.showingToOf", "Showing {{from}} to {{to}} of {{count}} entries")
                .replace("{{from}}", String(entriesStart))
                .replace("{{to}}", String(entriesEnd))
                .replace("{{count}}", String(totalEntries))}
            </MDTypography>
          </MDBox>
        )}
        {totalPages > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {(isServerSidePagination ? currentPage > 0 : canPreviousPage) && (
              <MDPagination item onClick={() => goToPageSafe(currentPage - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{ type: "number", min: 1, max: totalPages }}
                  value={customizedPageOptions[currentPage]}
                  onChange={handleInputPaginationValue}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {(isServerSidePagination ? currentPage < totalPages - 1 : canNextPage) && (
              <MDPagination item onClick={() => goToPageSafe(currentPage + 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  onPaginationChange: undefined,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
      onChange: PropTypes.func,
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    page: PropTypes.number,
    onPageChange: PropTypes.func,
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  totalRecords: PropTypes.number,
  onPaginationChange: PropTypes.func,
};

export default DataTable;
